
import { useState, useRef, useCallback } from 'react';

interface UploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (files: { url: string; name: string }[], category: string) => void;
}

const categoryOptions = ['주거 공간', '상업 공간', '리모델링'];

export default function UploadModal({ isOpen, onClose, onUpload }: UploadModalProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [previews, setPreviews] = useState<{ url: string; name: string; file: File }[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(categoryOptions[0]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ----------------------------------------------------------------------
  // Helper: process selected files and generate previews
  // ----------------------------------------------------------------------
  const handleFiles = useCallback((files: FileList | null) => {
    if (!files) return;
    const newPreviews: { url: string; name: string; file: File }[] = [];

    Array.from(files).forEach((file) => {
      if (!file.type.startsWith('image/')) return;

      // Guard against creating too many object URLs at once
      try {
        const url = URL.createObjectURL(file);
        newPreviews.push({ url, name: file.name, file });
      } catch (err) {
        console.error('Failed to create object URL for file', file, err);
      }
    });

    if (newPreviews.length) {
      setPreviews((prev) => [...prev, ...newPreviews]);
    }
  }, []);

  // ----------------------------------------------------------------------
  // Drag‑and‑drop handlers
  // ----------------------------------------------------------------------
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  // ----------------------------------------------------------------------
  // Remove a preview and clean up its object URL
  // ----------------------------------------------------------------------
  const removePreview = (index: number) => {
    setPreviews((prev) => {
      const updated = [...prev];
      const [removed] = updated.splice(index, 1);
      if (removed) {
        URL.revokeObjectURL(removed.url);
      }
      return updated;
    });
  };

  // ----------------------------------------------------------------------
  // Upload logic (simulated with a timeout)
  // ----------------------------------------------------------------------
  const handleUpload = () => {
    if (previews.length === 0) return;

    setIsUploading(true);

    // Simulate async upload – replace with real API call as needed
    setTimeout(() => {
      try {
        onUpload(
          previews.map((p) => ({ url: p.url, name: p.name })),
          selectedCategory
        );
        // Clean up object URLs after successful upload
        previews.forEach((p) => URL.revokeObjectURL(p.url));
        setPreviews([]);
        setSelectedCategory(categoryOptions[0]);
        onClose();
      } catch (err) {
        console.error('Upload failed', err);
        // Keep the previews so the user can retry
      } finally {
        setIsUploading(false);
      }
    }, 800);
  };

  // ----------------------------------------------------------------------
  // Close modal – clean up all resources
  // ----------------------------------------------------------------------
  const handleClose = () => {
    previews.forEach((p) => URL.revokeObjectURL(p.url));
    setPreviews([]);
    setSelectedCategory(categoryOptions[0]);
    onClose();
  };

  // ----------------------------------------------------------------------
  // Render
  // ----------------------------------------------------------------------
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-xl mx-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-6 border-b border-gray-100">
          <h3 className="text-xl font-bold text-gray-900">사진 추가</h3>
          <button
            onClick={handleClose}
            className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-900 transition-colors cursor-pointer rounded-full hover:bg-gray-100"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Body */}
        <div className="px-8 py-6 space-y-5">
          {/* Category selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">카테고리</label>
            <div className="flex items-center gap-2 flex-wrap">
              {categoryOptions.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-4 py-2 text-sm rounded-full transition-all whitespace-nowrap cursor-pointer ${
                    selectedCategory === cat
                      ? 'bg-gray-900 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {/* Drag & Drop area */}
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-all duration-300 ${
              isDragging ? 'border-[#D97757] bg-[#D97757]/5' : 'border-gray-200 hover:border-gray-400 bg-gray-50'
            }`}
          >
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-[#F5F2ED]">
              <i className="ri-upload-cloud-2-line text-3xl text-[#D97757]"></i>
            </div>
            <p className="text-base font-medium text-gray-700 mb-1">
              여기에 사진을 드래그하거나 클릭하여 선택
            </p>
            <p className="text-sm text-gray-400">JPG, PNG, WEBP (최대 10MB)</p>
          </div>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
          />

          {/* Previews */}
          {previews.length > 0 && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-3">
                선택된 사진 ({previews.length}장)
              </p>
              <div className="grid grid-cols-4 gap-3 max-h-48 overflow-y-auto">
                {previews.map((preview, index) => (
                  <div key={index} className="relative group rounded-lg overflow-hidden">
                    <div className="w-full h-24">
                      <img src={preview.url} alt={preview.name} className="w-full h-full object-cover" />
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removePreview(index);
                      }}
                      className="absolute top-1 right-1 w-6 h-6 flex items-center justify-center bg-black/60 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                    >
                      <i className="ri-close-line text-xs"></i>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="flex items-center justify-end gap-3 px-8 py-5 border-t border-gray-100">
          <button
            onClick={handleClose}
            className="px-6 py-2.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
          >
            취소
          </button>
          <button
            onClick={handleUpload}
            disabled={previews.length === 0 || isUploading}
            className="px-6 py-2.5 text-sm font-medium text-white bg-[#D97757] rounded-lg hover:bg-[#C86647] transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
          >
            {isUploading ? (
              <span className="flex items-center gap-2">
                <i className="ri-loader-4-line animate-spin"></i>
                업로드 중...
              </span>
            ) : (
              `업로드 (${previews.length})`
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
